import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { LogOut, Search, Loader2, CheckCircle, ExternalLink, PenLine, ArrowRight } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { OnboardingLayout, LoadingScreen } from "@/frontend/components/onboarding/Onboarding";
import { OrganizationCard } from "./OrganizationCard";
import { MobileAppSection } from "./MobileAppSection";
import { useConsent } from "@/frontend/components/consent-flow/useConsent";
import { ConsentScreen } from "@/frontend/components/consent-flow/ConsentScreen";
import { RequestFacilityLink } from "@/frontend/components/shared/RequestFacilityLink";
import { signOut } from "../../auth-client";
import { orpc } from "@/frontend/orpc-client";
import {
  useActiveOrgQuery,
  useFhirSourcesSearchQuery,
  useOnboardingSettingsQuery,
  usePopularFhirSourcesQuery,
  usePossiblePortalsQuery,
  useConnectedAccountsQuery,
  useOnboardingSources,
} from "@/frontend/queries/index";
import { useOnboardingStateQuery } from "@/frontend/queries/sign";
import { patientGreetingName } from "@/frontend/utils/patient-name";
import { type PossiblePortal } from "@/frontend/types/fhir";

type AuthenticatedViewProps = {
  session: { user: { id: string; name: string; email: string } };
  /** Set when the URL carries `?patient_session_id=` — switches to Connect Session UI. */
  patientSessionId?: string;
};

export function AuthenticatedView({ session, patientSessionId }: AuthenticatedViewProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  // A long full name wrapped the heading and pushed Sign out out of its row.
  const greetingName = patientGreetingName(session.user.name);

  // The terms lookup and the acceptance write both live in the
  // hook, shared with the dashboard's own connect surface.
  const consent = useConsent();

  // ── Connect Session mode ──────────────────────────────────────────────────
  // When ?patient_session_id= is present, fetch the patient_session via session-auth so
  // we can verify the cookie binding (server rejects with 403 if mismatched)
  // and adapt the UI accordingly.
  //
  // The patient_session_id is intentionally left in the URL — it's not sensitive
  // (the cookie does the actual auth; another browser pasting this URL gets
  // a 403 from getMyPatientSession), and refresh/share within the same browser
  // continues working naturally without an extra side-effect to strip it.
  const patientSessionQuery = useQuery({
    ...orpc.patientSession.getMyPatientSession.queryOptions({ input: { patient_session_id: patientSessionId ?? "" } }),
    enabled: !!patientSessionId,
    retry: false,
  });

  const patientSession = patientSessionQuery.data;
  const inPatientSessionMode = !!patientSessionId && !!patientSession;

  const completeMutation = useMutation(
    orpc.patientSession.completePatientSession.mutationOptions(),
  );

  const handleDoneInSession = async () => {
    if (!patientSession) return;
    const result = await completeMutation.mutateAsync({ patient_session_id: patientSession.id });
    window.location.assign(result.redirect_url);
  };

  /**
   * Confirmed exit from the consent flow. In session mode the patient came from
   * an app, so finish the session and hand them back; a magic-link patient has
   * nowhere to be sent, so the flow closes in place.
   */
  const handleLeaveSetup = () => {
    if (patientSession) {
      completeMutation.mutate(
        { patient_session_id: patientSession.id },
        { onSuccess: (result) => window.location.assign(result.redirect_url) },
      );
      return;
    }
    consent.closeSetup();
  };

  const { data: activeOrg, isLoading: isLoadingActiveOrg } = useActiveOrgQuery();
  const { data: searchResults, isLoading: isSearching } = useFhirSourcesSearchQuery(searchQuery);
  const { data: settings, isLoading: isLoadingSettings } = useOnboardingSettingsQuery();
  const { data: onboardingState, isLoading: isLoadingState } = useOnboardingStateQuery(
    !!settings?.askConsentSignature
  );
  const { data: possiblePortals, isLoading: isLoadingPortals } = usePossiblePortalsQuery();
  const { data: connectedAccounts, isLoading: isLoadingAccounts } = useConnectedAccountsQuery(true);
  const { data: popularSources } = usePopularFhirSourcesQuery();

  const {
    connectedProviderIds,
    connectedSources,
    recommendedSources: recommendedFromOrg,
  } = useOnboardingSources(
    session.user.id,
    possiblePortals as PossiblePortal[] | undefined,
    connectedAccounts
  );

  // In session mode the recommended list comes from the patient_session
  // (developer-provided, per-session), NOT the org's possible_patient_portal
  // catalog. Fall back to the org catalog only when not in session mode.
  const recommendedSources = inPatientSessionMode && patientSession?.recommended_fhir_sources
    ? patientSession.recommended_fhir_sources.filter((s) => !connectedProviderIds.has(s.fhirBaseUrl))
    : recommendedFromOrg;

  // Popular payer tiles (Aetna/Medicare/UHC) are always surfaced so patients don't
  // have to type their name to find them; de-duped against connected + recommended by fhirBaseUrl.
  const shownFhirBaseUrls = new Set<string>([
    ...connectedSources.map((s) => s.fhirBaseUrl),
    ...recommendedSources.map((s) => s.fhirBaseUrl),
  ]);
  const popularToShow = (popularSources ?? []).filter((s) => !shownFhirBaseUrls.has(s.fhirBaseUrl));
  const hasAnyPortals =
    connectedSources.length > 0 || recommendedSources.length > 0 || popularToShow.length > 0;

  if (isLoadingPortals || isLoadingAccounts || isLoadingSettings || (settings?.askConsentSignature && isLoadingState)) {
    return <LoadingScreen />;
  }

  // Session-mode: still loading the patient_session itself.
  if (patientSessionId && patientSessionQuery.isLoading) {
    return <LoadingScreen />;
  }

  // Session-mode: cookie not bound to this patient_session_id (or session expired).
  if (patientSessionId && patientSessionQuery.isError) {
    return (
      <OnboardingLayout>
        <div className="flex flex-col items-center justify-center min-h-[50vh] px-6 py-6 text-center">
          <h1 className="text-xl font-semibold mb-2">This connect link is no longer active</h1>
          <p className="text-sm text-muted-foreground mb-6">
            The link may have expired, or it was opened in a different browser. Ask the app that sent you here to generate a new link.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => { await signOut(); window.location.href = "/"; }}
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </Button>
        </div>
      </OnboardingLayout>
    );
  }

  // Session already completed or expired — don't show the picker.
  if (inPatientSessionMode && patientSession.status !== "open") {
    const label = patientSession.return_button_label ?? "your app";
    return (
      <OnboardingLayout>
        <div className="flex flex-col items-center justify-center min-h-[50vh] px-6 py-6 text-center">
          <h1 className="text-xl font-semibold mb-2">
            {patientSession.status === "complete" ? "This session is complete" : "This session has expired"}
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            {patientSession.status === "complete"
              ? "You've already finished this session. If you need to connect again, ask the app to create a new session."
              : "This session is no longer available. Ask the app that sent you here to generate a new link."}
          </p>
          <Button
            size="sm"
            onClick={() => { handleDoneInSession(); }}
          >
            {label}
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </OnboardingLayout>
    );
  }

  // ── Consent ──────────────────────────────────────────────────────────
  // Runs after the loading / 403 / completed / expired branches above, so a dead
  // link still reports itself as dead instead of opening a consent flow that
  // leads nowhere. Only patient actors reach this component (see routes/index.tsx),
  // and `getTermsAcceptance` is a patientProcedure, so org members and platform
  // admins never see these screens.
  if (consent.status !== "accepted") {
    // The org name and requester kind are part of the consent copy, so wait for
    // them rather than swap them in under the patient's eyes. `error` renders
    // its own retry, not a spinner.
    if (consent.status === "loading" || (consent.status === "needed" && isLoadingActiveOrg)) {
      return <LoadingScreen />;
    }
    return (
      <OnboardingLayout>
        <ConsentScreen
          consent={consent}
          organizationName={activeOrg?.name}
          requesterType={activeOrg?.requesterType}
          onLeave={handleLeaveSetup}
          isLeaving={completeMutation.isPending}
          leaveFailed={completeMutation.isError}
        />
      </OnboardingLayout>
    );
  }

  const returnLabel = patientSession?.return_button_label ?? "your app";

  return (
    <OnboardingLayout>
      <div className={`flex-1 flex flex-col px-6 py-6 ${inPatientSessionMode ? "pb-2" : ""}`}>
        {/* Welcome header. Session-mode hides Sign out; the Done CTA renders as a sticky footer below. */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold tracking-tight">
              {greetingName ? (
                <>Welcome, <span data-patient>{greetingName}</span></>
              ) : (
                "Hi there"
              )}
            </h1>
          </div>
          {!inPatientSessionMode && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground gap-1.5"
              onClick={async () => {
                await signOut();
                window.location.href = "/";
              }}
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </Button>
          )}
        </div>

        <p className="text-sm text-muted-foreground mb-6">
          Link your patient portals to securely share your medical records, lab results, and visit history. Just tap &quot;Connect&quot; and sign in with your existing portal credentials.
        </p>

        {/* Section 1: Provide your consent signature.
            Hidden in session-mode — the developer-API flow doesn't include the
            internal onboarding consent affordance; that's an org-internal concern. */}
        {!inPatientSessionMode && settings?.askConsentSignature && (
          <>
            <h2 className="text-sm font-semibold mb-3">Provide your signature</h2>

            {onboardingState?.signStatus === "completed" ? (
              <div className="mb-6">
                <div className="flex items-center gap-2.5 p-2.5 rounded-lg transition-all border bg-emerald-50 border-emerald-300">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-emerald-100">
                    <PenLine className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">Authorization signature</p>
                    <p className="text-xs text-muted-foreground">
                      Signed on{" "}
                      {onboardingState.updatedAt
                        ? new Date(onboardingState.updatedAt).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                        : "—"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 text-emerald-600 shrink-0">
                    <span className="text-xs font-medium">Signed</span>
                    <CheckCircle className="w-4 h-4" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <div className="flex items-center gap-2.5 p-2.5 rounded-lg transition-all border bg-muted/50 border-transparent">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-muted">
                    <PenLine className="w-4 h-4 text-foreground/60" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">Authorization signature</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => navigate({ to: "/sign", search: { continue: false } })}
                    className="gap-1 h-7 px-2.5 text-xs shrink-0"
                  >
                    Sign
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Section 2: Link your patient portals */}
        <div className="mb-6">
          <h2 className="text-sm font-semibold mb-3">Link your patient portals</h2>

          {hasAnyPortals ? (
            <div className="space-y-2 mb-3">
              {connectedSources.map((source) => (
                <OrganizationCard
                  key={source.fhirBaseUrl}
                  source={source}
                  isConnected={true}
                  organizationId={activeOrg?.id ?? ""}
                  organizationName={activeOrg?.name}
                  organizationIsMedblocks={settings?.organizationIsMedblocks ?? false}
                  patientSessionId={patientSessionId}
                />
              ))}
              {recommendedSources.map((source) => (
                <OrganizationCard
                  key={source.fhirBaseUrl}
                  source={source}
                  isConnected={false}
                  organizationId={activeOrg?.id ?? ""}
                  organizationName={activeOrg?.name}
                  organizationIsMedblocks={settings?.organizationIsMedblocks ?? false}
                  patientSessionId={patientSessionId}
                />
              ))}
              {popularToShow.map((source) => (
                <OrganizationCard
                  key={source.fhirBaseUrl}
                  source={source}
                  isConnected={false}
                  organizationId={activeOrg?.id ?? ""}
                  organizationName={activeOrg?.name}
                  organizationIsMedblocks={settings?.organizationIsMedblocks ?? false}
                  patientSessionId={patientSessionId}
                />
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground mb-3">
              No patient portals have been recommended yet.
            </p>
          )}

          {/* Search within this section */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search for other healthcare organizations..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {searchQuery.length >= 2 && (
            <div className="space-y-2 mt-2">
              {isSearching ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : (searchResults ?? []).length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-sm">
                  No organizations found
                </div>
              ) : (
                (searchResults ?? [])
                  .filter((s) => !connectedProviderIds.has(s.fhirBaseUrl))
                  .map((source) => (
                    <OrganizationCard
                      key={source.fhirBaseUrl}
                      source={source}
                      isConnected={false}
                      organizationId={activeOrg?.id ?? ""}
                      organizationName={activeOrg?.name}
                      organizationIsMedblocks={settings?.organizationIsMedblocks ?? false}
                      patientSessionId={patientSessionId}
                    />
                  ))
              )}
            </div>
          )}

          <RequestFacilityLink className="mt-3" />
        </div>

        {/* Section 3: Mobile App - only when admin has enabled it.
            Hidden in session mode — the developer-API patient session doesn't
            surface the dashboard's internal mobile-app affordance. */}
        {!inPatientSessionMode && settings?.showMobileApp && (
          <>
            <div className="border-t" />
            <div className="pt-6">
              <MobileAppSection />
            </div>
          </>
        )}
      </div>

      {/* Sticky bottom CTA — session-mode only. Black bg / white text per spec. */}
      {inPatientSessionMode && (
        <div className="sticky bottom-0 left-0 right-0 px-6 py-4 border-t bg-background">
          <Button
            size="lg"
            className="w-full bg-black text-white hover:bg-gray-900 dark:bg-white dark:text-black dark:hover:bg-gray-100 gap-2"
            onClick={handleDoneInSession}
            disabled={completeMutation.isPending}
          >
            {completeMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : null}
            {returnLabel}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </OnboardingLayout>
  );
}
